import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/design-system/Button'
import { Badge } from '@/design-system/primitives'
import { ApiError } from '@/lib/apiClient'

import { useAdminMutation, useAdminQuery } from '../api'
import type { Award, AwardResult, Candidate, CompetitionCategory, I18n } from '../types'
import { CollectionEditor, FormActions } from './CollectionEditor'
import { I18nField, SelectField, SwitchField, TextField, type I18nValue } from './fields'
import { MediaField } from './MediaField'
import { UndoPublication } from './UndoPublication'

/** Trois marches : au-delà, un podium devient un classement. */
const RANKS = [1, 2, 3] as const

/**
 * Les récompenses et leur podium — « Prix du public », « Best Mask »…
 *
 * L'API existait (créer un prix, saisir son podium, le révéler) ; aucun écran
 * ne s'en servait. Deux gestes séparés, comme pour le concours :
 *
 * * **saisir** le podium ne le montre à personne — l'équipe le prépare avant
 *   l'annonce ;
 * * **révéler** le rend public, rang par rang déjà saisi : un rang révélé
 *   ne se modifie plus. Une révélation par erreur se défait (« Masquer à
 *   nouveau », motif obligatoire) : le podium redevient privé et modifiable.
 *
 * Le titre Miss / Master, lui, ne se saisit pas ici : il découle des tours
 * et se proclame depuis « Votes ».
 */
export function AwardsPanel({
  eventId,
  competitions,
  candidates,
  label,
  can,
}: {
  eventId: string | undefined
  competitions: CompetitionCategory[]
  candidates: Candidate[]
  label: (value: I18n) => string
  can: (permission: string) => boolean
}) {
  const { t } = useTranslation()
  const path = `/admin/events/${eventId}/awards`
  const key = ['admin-awards', eventId]
  const query = useAdminQuery<Award[]>(key, path, eventId, {
    enabled: can('competition.award.read'),
  })
  const refresh = [key, ['competition-awards'], ['admin-candidates', eventId]]

  const create = useAdminMutation<Award, Record<string, unknown>>('post', () => path, eventId, refresh)
  const update = useAdminMutation<Award, { id: string } & Record<string, unknown>>(
    'patch',
    ({ id }) => `${path}/${id}`,
    eventId,
    refresh,
  )
  const remove = useAdminMutation<void, { id: string }>(
    'delete',
    ({ id }) => `${path}/${id}`,
    eventId,
    refresh,
  )

  if (!can('competition.award.read')) return null

  const busy = create.isPending || update.isPending || remove.isPending
  const contestName = (id: string | null) =>
    id ? label(competitions.find((entry) => entry.id === id)?.name_i18n ?? {}) : ''

  return (
    <CollectionEditor<Award>
      title={t('admin.awards.title')}
      description={t('admin.awards.hint')}
      items={query.data ?? []}
      busy={busy}
      error={create.error ?? update.error ?? remove.error ?? query.error}
      emptyLabel={t('admin.awards.empty')}
      createLabel={can('competition.award.create') ? t('admin.awards.add') : null}
      onDelete={(award) => remove.mutate({ id: award.id })}
      // Un podium annoncé appartient à la soirée : le serveur refuserait.
      canDelete={(award) =>
        can('competition.award.delete') && !award.results.some((row) => row.is_revealed)
      }
      summarize={(award) => ({
        title: label(award.name_i18n),
        subtitle: [contestName(award.competition_category_id), label(award.prize_i18n)]
          .filter((part) => part && part !== '—')
          .join(' · '),
        dimmed: !award.is_active,
        badges: award.results.some((row) => row.is_revealed)
          ? [{ label: t('admin.awards.revealed'), tone: 'success' as const }]
          : award.results.length > 0
            ? [{ label: t('admin.awards.podiumReady'), tone: 'warning' as const }]
            : [],
      })}
      renderForm={(award, close) => (
        <AwardForm
          award={award}
          eventId={eventId}
          competitions={competitions}
          candidates={candidates}
          label={label}
          can={can}
          busy={busy}
          onCancel={close}
          onSubmit={(payload) => {
            const done = { onSuccess: () => close() }
            if (award) update.mutate({ id: award.id, ...payload }, done)
            else create.mutate(payload, done)
          }}
        />
      )}
    />
  )
}

function AwardForm({
  award,
  eventId,
  competitions,
  candidates,
  label,
  can,
  busy,
  onSubmit,
  onCancel,
}: {
  award: Award | null
  eventId: string | undefined
  competitions: CompetitionCategory[]
  candidates: Candidate[]
  label: (value: I18n) => string
  can: (permission: string) => boolean
  busy: boolean
  onSubmit: (payload: Record<string, unknown>) => void
  onCancel: () => void
}) {
  const { t } = useTranslation()
  const [name, setName] = useState<I18nValue>(award?.name_i18n ?? {})
  const [description, setDescription] = useState<I18nValue>(award?.description_i18n ?? {})
  const [prize, setPrize] = useState<I18nValue>(award?.prize_i18n ?? {})
  const [contest, setContest] = useState(award?.competition_category_id ?? '')
  const [mediaId, setMediaId] = useState<string | null>(award?.media_id ?? null)
  const [active, setActive] = useState(award?.is_active ?? true)
  const canSave = award ? can('competition.award.update') : can('competition.award.create')

  return (
    <div>
      <div className="grid gap-5 sm:grid-cols-2">
        <I18nField label={t('admin.awards.name')} value={name} onChange={setName} />
        <I18nField label={t('admin.awards.prize')} value={prize} onChange={setPrize} />
        <I18nField
          label={t('admin.food.descriptionField')}
          multiline
          value={description}
          onChange={setDescription}
        />
        <SelectField
          label={t('admin.awards.contest')}
          hint={t('admin.awards.contestHint')}
          value={contest}
          onChange={setContest}
          options={[
            { value: '', label: t('admin.awards.anyContest') },
            ...competitions.map((entry) => ({ value: entry.id, label: label(entry.name_i18n) })),
          ]}
        />
        <MediaField
          label={t('admin.awards.media')}
          value={mediaId}
          eventId={eventId}
          onChange={setMediaId}
        />
        <SwitchField
          label={t('admin.active')}
          hint={t('admin.activeHint')}
          value={active}
          onChange={setActive}
        />
      </div>
      {canSave ? (
        <FormActions
          busy={busy || !Object.values(name).some((value) => value.trim())}
          onCancel={onCancel}
          onSubmit={() =>
            onSubmit({
              name_i18n: name,
              description_i18n: description,
              prize_i18n: prize,
              competition_category_id: contest || null,
              media_id: mediaId,
              is_active: active,
            })
          }
        />
      ) : null}

      {award ? (
        <Podium
          award={award}
          eventId={eventId}
          candidates={candidates.filter(
            (candidate) =>
              ['approved', 'finalist', 'winner'].includes(candidate.status) &&
              (!award.competition_category_id ||
                candidate.competition_category_id === award.competition_category_id),
          )}
          can={can}
        />
      ) : null}
    </div>
  )
}

/**
 * Le podium d'un prix : saisi en privé, puis révélé d'un geste.
 *
 * Exporté pour le conducteur du MC : c'est lui qui l'annonce, il le saisit et
 * le révèle depuis son écran, sans la fiche du prix autour.
 */
export function Podium({
  award,
  eventId,
  candidates,
  can,
}: {
  award: Award
  eventId: string | undefined
  candidates: Candidate[]
  can: (permission: string) => boolean
}) {
  const { t } = useTranslation()
  const existing = (rank: number): AwardResult | undefined =>
    award.results.find((row) => row.rank === rank)
  const [entries, setEntries] = useState<Record<number, { candidate: string; label: string }>>(
    () =>
      Object.fromEntries(
        RANKS.map((rank) => [
          rank,
          {
            candidate: existing(rank)?.candidate_id ?? '',
            label: existing(rank)?.label_override ?? '',
          },
        ]),
      ),
  )
  const [confirm, setConfirm] = useState(false)

  const path = `/admin/events/${eventId}/awards/${award.id}`
  const refresh = [['admin-awards', eventId], ['competition-awards'], ['admin-candidates', eventId]]
  const save = useAdminMutation<AwardResult[], { results: unknown[] }>(
    'put',
    () => `${path}/results`,
    eventId,
    refresh,
  )
  const reveal = useAdminMutation<AwardResult[], Record<string, never>>(
    'post',
    () => `${path}/reveal`,
    eventId,
    refresh,
  )

  const unreveal = useAdminMutation<AwardResult[], { reason: string }>(
    'post',
    () => `${path}/unreveal`,
    eventId,
    refresh,
  )

  const canEdit = can('competition.award.update')
  const canReveal = can('competition.result.reveal')
  const candidateName = (candidate: Candidate) =>
    [
      candidate.candidate_number ? `${candidate.candidate_number}.` : null,
      candidate.stage_name ?? `${candidate.first_name} ${candidate.last_name}`,
    ]
      .filter(Boolean)
      .join(' ')

  // Seuls les rangs remplis partent : un podium à deux marches est un podium.
  // Un rang déjà révélé ne se renvoie pas — le serveur le refuserait.
  const filled = RANKS.filter((rank) => !existing(rank)?.is_revealed)
    .map((rank) => ({
      rank,
      candidate_id: entries[rank]?.candidate || null,
      label_override: entries[rank]?.label.trim() || null,
    }))
    .filter((row) => row.candidate_id || row.label_override)
  const pending = award.results.some((row) => !row.is_revealed)
  const revealed = award.results.some((row) => row.is_revealed)
  const error = save.error ?? reveal.error ?? unreveal.error

  return (
    <div className="mt-8 border-t border-line-subtle pt-6">
      <p className="text-caption uppercase tracking-wide text-content-muted">
        {t('admin.awards.podium')}
      </p>
      <p className="mt-1 text-xs text-content-muted">{t('admin.awards.podiumHint')}</p>

      <ol className="mt-4 space-y-4">
        {RANKS.map((rank) => {
          const row = existing(rank)
          const locked = Boolean(row?.is_revealed) || !canEdit
          return (
            <li key={rank} className="rounded-md border border-line-subtle px-3 py-3">
              <div className="mb-2 flex items-center gap-2">
                <span className="font-display tabular-nums text-champagne-300">{rank}</span>
                {row?.is_revealed ? (
                  <Badge tone="success">{t('admin.awards.revealed')}</Badge>
                ) : null}
              </div>
              {locked ? (
                <p className="text-sm text-content-secondary">
                  {(() => {
                    const chosen = candidates.find((entry) => entry.id === row?.candidate_id)
                    return (chosen ? candidateName(chosen) : row?.label_override) || '—'
                  })()}
                </p>
              ) : (
                <div className="grid gap-3 sm:grid-cols-2">
                  <SelectField
                    label={t('admin.awards.candidate')}
                    value={entries[rank]?.candidate ?? ''}
                    onChange={(value) =>
                      setEntries((current) => ({
                        ...current,
                        [rank]: { candidate: value, label: current[rank]?.label ?? '' },
                      }))
                    }
                    options={[
                      { value: '', label: t('admin.awards.noCandidate') },
                      ...candidates.map((candidate) => ({
                        value: candidate.id,
                        label: candidateName(candidate),
                      })),
                    ]}
                  />
                  <TextField
                    label={t('admin.awards.labelOverride')}
                    hint={t('admin.awards.labelOverrideHint')}
                    value={entries[rank]?.label ?? ''}
                    onChange={(value) =>
                      setEntries((current) => ({
                        ...current,
                        [rank]: { candidate: current[rank]?.candidate ?? '', label: value },
                      }))
                    }
                  />
                </div>
              )}
            </li>
          )
        })}
      </ol>

      <div className="mt-4 flex flex-wrap gap-2">
        {canEdit ? (
          <Button
            size="sm"
            variant="outline"
            disabled={save.isPending || filled.length === 0}
            onClick={() => save.mutate({ results: filled })}
          >
            {t('admin.awards.savePodium')}
          </Button>
        ) : null}
        {canReveal && pending ? (
          confirm ? (
            <>
              <p className="w-full text-caption text-state-warning">
                {t('admin.awards.revealWarning')}
              </p>
              <Button
                size="sm"
                variant="ember"
                disabled={reveal.isPending}
                onClick={() => reveal.mutate({}, { onSettled: () => setConfirm(false) })}
              >
                {t('admin.awards.revealConfirm')}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setConfirm(false)}>
                {t('admin.cancel')}
              </Button>
            </>
          ) : (
            <Button size="sm" variant="champagne" onClick={() => setConfirm(true)}>
              {t('admin.awards.reveal')}
            </Button>
          )
        ) : null}
        {/* Même droit que pour révéler : qui peut annoncer peut retirer. */}
        {canReveal && revealed && !confirm ? (
          <UndoPublication
            label={t('admin.undo.awardUnreveal')}
            confirmLabel={t('admin.undo.awardUnrevealConfirm')}
            warnings={[t('admin.undo.awardUnrevealWarning'), t('admin.undo.noMail')]}
            busy={unreveal.isPending}
            onConfirm={(reason) => unreveal.mutateAsync({ reason })}
          />
        ) : null}
      </div>
      {save.isSuccess ? (
        <p className="mt-2 text-caption text-state-success">{t('admin.awards.podiumSaved')}</p>
      ) : null}
      {error instanceof ApiError ? (
        <p className="mt-2 text-sm text-state-danger">{error.message}</p>
      ) : null}
    </div>
  )
}
